import { useEffect } from 'react'
import { Box, Checkbox, Message, Text } from 'ui'
import { Controller, useFormContext } from 'react-hook-form'
import { track } from 'lib/amplitude'
import { useIsCaliforniaDrivingShift } from '../../useIsCaliforniaDrivingShift'
import { ConfirmForm } from '../../schemas'

interface CaliforniaDrivingAcknowledgementProps {
  onAcknowledgementChange: (isAcknowledged: boolean) => void
}

export const CaliforniaDrivingAcknowledgement = ({
  onAcknowledgementChange,
}: CaliforniaDrivingAcknowledgementProps) => {
  const { control, watch } = useFormContext<ConfirmForm>()
  const isCaliforniaDrivingShift = useIsCaliforniaDrivingShift()

  const isAcknowledged = watch('hasAcknowledgedOddbRequirements')

  useEffect(() => {
    if (isCaliforniaDrivingShift) {
      track('impression, ShiftEditor_CA_driving_acknowledgement')
    }
  }, [isCaliforniaDrivingShift])

  useEffect(() => {
    onAcknowledgementChange(!isCaliforniaDrivingShift || isAcknowledged)
  }, [isCaliforniaDrivingShift, isAcknowledged, onAcknowledgementChange])

  if (!isCaliforniaDrivingShift) {
    return null
  }

  return (
    <Box mt={4}>
      <Message variant={'info'} hideIcon py={3} px={3}>
        <Controller
          name={'hasAcknowledgedOddbRequirements'}
          control={control}
          render={({ field }) => (
            <Checkbox
              value={field.value}
              onChange={(checked) => {
                field.onChange(checked)
                track('click, ShiftEditor_CA_driving_acknowledgement_toggle', {
                  acknowledged: checked,
                })
              }}
            >
              <Text fontWeight={2}>
                I acknowledge these California requirements for Delivery Blocks.
              </Text>
            </Checkbox>
          )}
        />
        <Box mt={2}>
          <Text>
            The following rules apply to all California 1099 Delivery Blocks:
          </Text>
        </Box>
        <Box
          as="ul"
          mt={2}
          mb={0}
          style={{ paddingLeft: '15px', listStyleType: 'disc' }}
        >
          <li style={{ marginBottom: '7.5px' }}>
            Delivery drivers may only perform delivery-related activities during
            Delivery Blocks (e.g., selecting and collecting the items to be
            delivered and delivering the selected items to customers)
          </li>
          <li style={{ marginBottom: '7.5px' }}>
            All delivery stops within the Delivery Block must be within a
            50-mile radius of the pick-up location.
          </li>
          <li style={{ marginBottom: '7.5px' }}>
            Drivers can be offered the opportunity to complete more than one
            delivery request on the Delivery Blocks
          </li>
          <li style={{ marginBottom: '7.5px' }}>
            Each delivery request must consist of no more than 12 distinct
            orders placed by different customers
          </li>
          <li>
            Drivers must be allowed to voluntarily decide whether to accept or
            decline each delivery request offered to them on a shift, and no
            adverse action can be taken against a driver who decides to decline
            a delivery request
          </li>
        </Box>
      </Message>
      {!isAcknowledged && (
        <Message variant={'warning'} mt={3}>
          You must acknowledge and adhere to the above Delivery Block
          requirements to post California Delivery Blocks to be performed by
          independent contractors. If you have any questions or need help,
          please reach out to WorkWhile Support or your Account Manager.
        </Message>
      )}
    </Box>
  )
}
